'use client'
import * as React from 'react'

import type { ContentCountFavoritesQuery } from '@web/gen/content'
import { useContentCountFavoritesQuery } from '@web/gen/content'

const defaultFavoritesState: ContentCountFavoritesQuery['contentCountFavorites'] & {
  productId: string
  refetch?: () => void
  isLoading: boolean
} = {
  productId: '',
  total: 0,
  users: [],
  isLoading: true,
}

const FavoritesContext = React.createContext(defaultFavoritesState)

interface ContextProviderProps extends React.PropsWithChildren {
  productId: string
  total: number
  users: ContentCountFavoritesQuery['contentCountFavorites']['users']
  isLoading?: boolean
}

export function FavoritesContextProvider({
  productId,
  total,
  users,
  children,
}: ContextProviderProps) {
  const { refetch, data, isPending } = useContentCountFavoritesQuery(
    { productId },
    {
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      staleTime: 60 * 1000,
    },
  )
  const totalLikes = data?.contentCountFavorites?.total ?? total
  const usersTotal = data?.contentCountFavorites?.users ?? users

  return (
    <FavoritesContext.Provider
      value={{
        ...data?.contentCountFavorites,
        total: totalLikes,
        users: usersTotal,
        refetch,
        productId,
        isLoading: isPending,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  )
}

export function useFavoritesContext() {
  const context = React.useContext(FavoritesContext)
  if (!context) {
    throw new Error('`useFavoritesContext()` must be used within a `<FavoritesContextProvider />')
  }
  return React.useContext(FavoritesContext)
}

export function useFavoriteTotal() {
  return useFavoritesContext().total
}

export function useFavoriteUsers() {
  return useFavoritesContext().users
}

export function useFavoriteRefetch() {
  return useFavoritesContext().refetch
}

export function useIsLoadingFavorites() {
  return useFavoritesContext().isLoading
}
